import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import ApiClient from '../Services/ApiClient';
import { Patient } from '../../Models/Patient';

export interface PatientsState {
  items: Patient[];
  loading: boolean;
  error?: string | null;
  // store removed items while awaiting server confirmation so we can rollback if needed
  pendingRemovals: Record<number, Patient>;
}

const initialState: PatientsState = {
  items: [],
  loading: false,
  error: null,
  pendingRemovals: {}
};

// Async thunks (kept for non-optimistic flows and initial fetch)
export const fetchPatients = createAsyncThunk('patients/fetch', async () => {
  const res = await ApiClient.get('/patients');
  return res as Patient[];
});

export const updatePatient = createAsyncThunk('patients/update', async (patient: Patient) => {
  await ApiClient.put(`/patients/${patient.id}`, patient);
  return patient;
});

const slice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    // optimistic add: insert a temp patient (with negative id) immediately
    addPatientOptimistic(state, action: PayloadAction<Patient>) {
      state.items.unshift(action.payload);
    },
    // replace temporary patient (tempId) with server-provided patient
    replaceTempPatient(state, action: PayloadAction<{ tempId: number; patient: Patient }>) {
      const idx = state.items.findIndex(p => p.id === action.payload.tempId);
      if (idx >= 0) state.items[idx] = action.payload.patient;
    },
    // rollback optimistic add
    rollbackAdd(state, action: PayloadAction<number>) {
      state.items = state.items.filter(p => p.id !== action.payload);
    },

    // optimistic remove: move patient to pendingRemovals and remove from items
    removePatientOptimistic(state, action: PayloadAction<number>) {
      const id = action.payload;
      const idx = state.items.findIndex(p => p.id === id);
      if (idx >= 0) {
        state.pendingRemovals[id] = state.items[idx];
        state.items.splice(idx, 1);
      }
    },
    // confirm server deletion: remove pending removal entry
    confirmRemove(state, action: PayloadAction<number>) {
      delete state.pendingRemovals[action.payload];
    },
    // rollback removal: restore patient from pendingRemovals
    rollbackRemove(state, action: PayloadAction<number>) {
      const id = action.payload;
      const original = state.pendingRemovals[id];
      if (original) {
        state.items.unshift(original);
        delete state.pendingRemovals[id];
      }
    },

    setPatients(state, action: PayloadAction<Patient[]>) {
      state.items = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatients.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchPatients.fulfilled, (s, a) => { s.loading = false; s.items = a.payload; })
      .addCase(fetchPatients.rejected, (s, a) => { s.loading = false; s.error = a.error.message || 'Failed' })

      .addCase(updatePatient.fulfilled, (s, a) => {
        s.items = s.items.map(p => p.id === a.payload.id ? a.payload : p);
      });
  }
});

export const {
  addPatientOptimistic,
  replaceTempPatient,
  rollbackAdd,
  removePatientOptimistic,
  confirmRemove,
  rollbackRemove,
  setPatients
} = slice.actions;

export default slice.reducer;